import { useCallback, useReducer } from "react";

type TankBatteryViewStateType = {
    // Options state
    filterText: string;

    // Other state
    isNoData: boolean;
    isOptionsDirty: boolean;
    downloadUid: string | undefined;
    refreshing: boolean;
};

const initialState: TankBatteryViewStateType = {
    filterText: "",
    isNoData: false,
    isOptionsDirty: false,
    downloadUid: undefined,
    refreshing: false
};

enum TankBatteryViewActionEnum {
    ResetOptionsRequested = "RESET_OPTIONS_REQUESTED",
    FilterTextChanged = "FILTER_TEXT_CHANGED",
    NoDataChanged = "NO_DATA_CHANGED",
    DownloadUidChanged = 'TANKBATTERY_VIEW_DOWNLOAD_UID_CHANGED',
    RefreshingChanged = 'TANKBATTERY_REFRESHING_CHANGED'
}

type TankBatteryViewActionTypes = {
    type: TankBatteryViewActionEnum.ResetOptionsRequested;
} | {
    type: TankBatteryViewActionEnum.FilterTextChanged;
    filterText: string;
} | {
    type: TankBatteryViewActionEnum.DownloadUidChanged;
    payload: string | undefined;
} | {
    type: TankBatteryViewActionEnum.NoDataChanged;
    isNoData: boolean;
} | {
    type: TankBatteryViewActionEnum.RefreshingChanged;
    payload: boolean;
};


function reducer(state: TankBatteryViewStateType, action: TankBatteryViewActionTypes): TankBatteryViewStateType {

    const copy = { ...state };
    switch (action.type) {

        case TankBatteryViewActionEnum.ResetOptionsRequested:
            copy.filterText = initialState.filterText;
            copy.isOptionsDirty = false;
            return copy;

        case TankBatteryViewActionEnum.FilterTextChanged:
            copy.filterText = action.filterText;
            copy.isOptionsDirty = isOptionsDirty(copy);
            return copy;

        case TankBatteryViewActionEnum.NoDataChanged:
            copy.isNoData = action.isNoData;
            return copy;

        case TankBatteryViewActionEnum.DownloadUidChanged:
            copy.downloadUid = action.payload;
            return copy;

        case TankBatteryViewActionEnum.RefreshingChanged:
            copy.refreshing = action.payload;
            return copy;
    
            default:
            return state;
    }
}

function isOptionsDirty(state: TankBatteryViewStateType) {
    return state.filterText !== initialState.filterText;
}

type UseTankBatteryViewStateType = {
    state: TankBatteryViewStateType;
    resetOptions: () => void;
    setFilterText: (filterText: string) => void;
    setNoData: (isNoData: boolean) => void;
    setDownloadUid: (value: string | undefined) => void;
    setRefreshing: (value: boolean) => void;
};

export default function useTankBatteryViewState(): UseTankBatteryViewStateType {
    const [state, dispatch] = useReducer(reducer, initialState);

    const resetOptions = useCallback(() => dispatch({
            type: TankBatteryViewActionEnum.ResetOptionsRequested
        }), []);

    const setFilterText = useCallback((filterText: string) => dispatch({
            type: TankBatteryViewActionEnum.FilterTextChanged,
            filterText: filterText
        }), []);

    const setNoData = useCallback((isNoData: boolean) => dispatch({
            type: TankBatteryViewActionEnum.NoDataChanged,
            isNoData: isNoData
        }), []);

    const setDownloadUid = useCallback((value: string | undefined) => dispatch({
        type: TankBatteryViewActionEnum.DownloadUidChanged,
        payload: value
    }), []);

    const setRefreshing = useCallback((value: boolean) => dispatch({
        type: TankBatteryViewActionEnum.RefreshingChanged,
        payload: value
    }), []);

    return {
        state,
        resetOptions,
        setFilterText,
        setNoData,
        setDownloadUid,
        setRefreshing
    };
}    
